// Which group the Routines rail highlights, from where each group currently sits in the viewport.
//
// The desktop list is two columns: the group titles on the left, one long stream of cards on the right
// (Figma 18352-340460). The rail is not just a menu — it also REPORTS position, so scrolling the stream
// moves the highlight. Both directions go through this one rule so the click and the scroll can never
// disagree about which group is current.
//
// Import-free and pure, so the rule is pinned by a test rather than by driving a scroll container.

/** One group's current offset from the top of the scroll viewport, in px. Negative = scrolled past. */
export interface RailSectionTop {
  key: string;
  top: number;
}

/**
 * The group to highlight.
 *
 * The reader is "in" the last group whose top edge has passed the reading line — a band below the
 * sticky page chrome, not the very top of the viewport, because a group whose heading is still a
 * sliver below the fold is not the one being read.
 *
 * Two edge cases, both real on this screen:
 *  - **Nothing has passed the line yet** (the list is scrolled to the very top) → the FIRST group. The
 *    rail must always name something; an empty rail on a populated list reads as broken.
 *  - **An empty list** → nothing to name, so `null`.
 *
 * Ties go to the later group: two groups can share a top when one is empty of visible cards, and the
 * one the reader is scrolling INTO is the useful answer.
 */
export function activeRailSection(tops: readonly RailSectionTop[], readingLine = 160): string | null {
  if (tops.length === 0) return null;
  let active = tops[0].key;
  for (const section of tops) {
    if (section.top <= readingLine) active = section.key;
  }
  return active;
}
